import React, { useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import { v4 as uuidv4 } from 'uuid';
import { useLocation } from 'react-router-dom';
import API_BASE_URL from '../config';
import { renderWithLinks } from '../utils/linkify';
import './ChatWidget.css';

const SOCKET_URL = API_BASE_URL.startsWith('http')
    ? API_BASE_URL.replace('/api', '')
    : (window.location.hostname === 'localhost' ? 'http://localhost:4000' : window.location.origin);
const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

const ChatWidget = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [sessionId, setSessionId] = useState(null);
    const [guestName, setGuestName] = useState(localStorage.getItem('guestChatName') || '');
    const [isStarted, setIsStarted] = useState(false); // only true after name submitted
    const [isOfflineMode, setIsOfflineMode] = useState(false);
    const [showCloseConfirm, setShowCloseConfirm] = useState(false);
    const [isSessionEnded, setIsSessionEnded] = useState(false); // set when admin closes the session
    const [isConnecting, setIsConnecting] = useState(false);
    const [connectionError, setConnectionError] = useState(null);
    const [isConnected, setIsConnected] = useState(false);
    const [showGreeting, setShowGreeting] = useState(false);

    // First-time greeting message that auto-dismisses so it doesn't block elements
    useEffect(() => {
        const path = window.location.pathname;
        if (path.startsWith('/admin')) return;
        const alreadyShown = sessionStorage.getItem('chatGreetingShown') || localStorage.getItem('chatGreetingShown');
        if (!alreadyShown) {
            const showTimer = setTimeout(() => {
                setShowGreeting(true);
                localStorage.setItem('chatGreetingShown', 'true');
                sessionStorage.setItem('chatGreetingShown', 'true');
            }, 1200);

            // Auto-hide after 6.5s so it disappears and never gets in front of content
            const hideTimer = setTimeout(() => {
                setShowGreeting(false);
            }, 7500);

            return () => {
                clearTimeout(showTimer);
                clearTimeout(hideTimer);
            };
        }
    }, []);

    const socketRef = useRef(null);       // keeps socket reference without causing re-renders
    const sessionIdRef = useRef(null);    // avoids stale closure in idle timer
    const idleTimerRef = useRef(null);    // 30-min idle handle
    const messagesEndRef = useRef(null);
    const location = useLocation();

    // Keep sessionIdRef in sync
    useEffect(() => { sessionIdRef.current = sessionId; }, [sessionId]);

    // Auto-scroll
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, isOpen]);

    // Check offline mode once on mount via REST (no socket needed before chat starts)
    useEffect(() => {
        fetch(`${API_BASE_URL}/chats/offline-mode`)
            .then(res => res.json())
            .then(data => {
                if (data && typeof data.isOfflineMode === 'boolean') {
                    setIsOfflineMode(data.isOfflineMode);
                }
            })
            .catch(err => console.error('Failed to load offline mode', err));
    }, []);

    // ── Idle timer ───────────────────────────────────────────────────────────
    const resetIdleTimer = useCallback(() => {
        if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
        idleTimerRef.current = setTimeout(() => {
            // Auto-close after 30 min idle — no prompt, just silently end
            endSession(true);
        }, IDLE_TIMEOUT_MS);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ── End session ──────────────────────────────────────────────────────────
    const endSession = useCallback(async (silent = false) => {
        // Clear idle timer
        if (idleTimerRef.current) { clearTimeout(idleTimerRef.current); idleTimerRef.current = null; }

        // Notify server
        const sid = sessionIdRef.current;
        if (sid) {
            try {
                await fetch(`${API_BASE_URL}/chats/session/${sid}/close`, {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                });
            } catch { }
        }

        // Disconnect socket
        if (socketRef.current) {
            socketRef.current.close();
            socketRef.current = null;
        }

        // Clear persisted guest identity
        localStorage.removeItem('guestChatId');
        localStorage.removeItem('guestChatName');

        // Reset UI state
        setSessionId(null);
        setIsStarted(false);
        setGuestName('');
        setMessages([]);
        setIsOpen(false);
    }, []);

    // ── Close button handler ─────────────────────────────────────────────────
    const handleCloseWidget = useCallback(() => {
        if (isStarted && sessionIdRef.current) {
            // Active session — show in-widget confirmation dialog
            setShowCloseConfirm(true);
        } else {
            // No active session — just collapse the widget
            setIsOpen(false);
        }
    }, [isStarted]);

    // ── Start chat (submit name) — THIS is where the socket connects ─────────
    const startChat = useCallback((e) => {
        e.preventDefault();
        if (!guestName.trim()) return;

        localStorage.setItem('guestChatName', guestName);

        // ── Lazy socket connection ──────────────────────────────────────────
        setIsConnecting(true);
        setConnectionError(null);

        const socket = io(SOCKET_URL, {
            path: '/api/socket.io',
            withCredentials: true,
            transports: ['websocket', 'polling'] // force websocket first but allow polling fallback
        });
        socketRef.current = socket;

        let guestChatId = localStorage.getItem('guestChatId');
        if (!guestChatId) {
            guestChatId = uuidv4();
            localStorage.setItem('guestChatId', guestChatId);
        }

        const setupSession = () => {
            console.log('[Chat] Socket connected, creating session...');
            setIsConnected(true);
            socket.emit('create_session', { guestId: guestChatId, guestName: guestName.trim() }, (response) => {
                setIsConnecting(false);
                if (response && response.success && response.session) {
                    setSessionId(response.session.id);
                    // Load history
                    fetch(`${API_BASE_URL}/chats/session/${response.session.id}?guestId=${guestChatId}`)
                        .then(res => res.json())
                        .then(data => { if (data.messages) setMessages(data.messages); })
                        .catch(err => console.error('[Chat] Failed to load history', err));
                    // Start idle timer
                    resetIdleTimer();
                } else {
                    console.error('[Chat] Session creation failed:', response?.error);
                    setConnectionError('Failed to start chat session. Please try again.');
                }
            });
        };

        if (socket.connected) {
            setupSession();
        } else {
            socket.once('connect', setupSession);
        }

        socket.on('receive_message', (msg) => {
            setMessages(prev => [...prev, msg]);
            resetIdleTimer(); // incoming message resets idle
        });

        socket.on('chat_offline_mode_changed', (data) => {
            if (data && typeof data.isOfflineMode === 'boolean') {
                setIsOfflineMode(data.isOfflineMode);
            }
        });

        // Agent closed the session from the admin console
        socket.on('session_closed', () => {
            if (idleTimerRef.current) { clearTimeout(idleTimerRef.current); idleTimerRef.current = null; }
            setIsSessionEnded(true);
            setShowCloseConfirm(false);
            localStorage.removeItem('guestChatId');
            localStorage.removeItem('guestChatName');
        });

        socket.on('connect_error', (err) => {
            console.error('[Chat] Connection error:', err.message);
            setIsConnecting(false);
            setIsConnected(false);
            setConnectionError('Unable to connect to chat server.');
        });

        socket.on('disconnect', (reason) => {
            console.log('[Chat] Disconnected:', reason);
            setIsConnected(false);
        });

        setIsStarted(true);
    }, [guestName, resetIdleTimer]);

    // ── Send message ─────────────────────────────────────────────────────────
    const sendMessage = useCallback((e) => {
        e.preventDefault();
        const socket = socketRef.current;
        if (!newMessage.trim() || !socket || !sessionId) return;

        socket.emit('send_message', {
            sessionId,
            senderType: 'client',
            content: newMessage
        }, (res) => {
            if (res.success) {
                setMessages(prev => [...prev, res.message]);
                resetIdleTimer(); // outgoing message resets idle
            } else {
                console.error('Failed to send message', res.error);
            }
        });

        setNewMessage('');
    }, [newMessage, sessionId, resetIdleTimer]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
            if (socketRef.current) socketRef.current.close();
        };
    }, []);

    if (location.pathname.startsWith('/admin')) return null;

    return (
        <div className="chat-widget-container">
            {isOpen && (
                <div className="chat-window">
                    <div className="chat-header">
                        <h4>Live Support</h4>
                        <button className="chat-close" onClick={handleCloseWidget}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                        </button>
                    </div>

                    {isOfflineMode && (
                        <div className="offline-banner" style={{ background: '#fffbeb', color: '#b45309', padding: '10px 16px', fontSize: '13.5px', textAlign: 'center', borderBottom: '1px solid #fde68a', lineHeight: '1.5', fontWeight: 500 }}>
                            Operatorët tanë janë aktualisht jashtë linje. Ju lutemi lini një mesazh dhe ne do t'ju përgjigjemi sa më shpejt të jetë e mundur.
                        </div>
                    )}

                    {/* ── Close confirmation overlay ────────────────────────── */}
                    {showCloseConfirm && (
                        <div style={{
                            position: 'absolute', inset: 0,
                            background: 'rgba(15,23,42,0.55)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            zIndex: 10, borderRadius: 'inherit',
                            backdropFilter: 'blur(2px)'
                        }}>
                            <div style={{
                                background: '#fff', borderRadius: '14px',
                                padding: '28px 24px', width: '82%',
                                boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                                textAlign: 'center'
                            }}>
                                <div style={{ fontSize: '28px', marginBottom: '8px' }}>💬</div>
                                <p style={{ margin: '0 0 6px', fontWeight: 700, fontSize: '15px', color: '#0f172a' }}>End this chat?</p>
                                <p style={{ margin: '0 0 20px', fontSize: '13px', color: '#64748b', lineHeight: 1.5 }}>
                                    Your session will be closed and you won't be able to continue this conversation.
                                </p>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <button
                                        onClick={() => setShowCloseConfirm(false)}
                                        style={{
                                            flex: 1, padding: '10px', borderRadius: '8px',
                                            border: '1px solid #e2e8f0', background: '#f8fafc',
                                            color: '#334155', fontWeight: 600,
                                            fontSize: '13px', cursor: 'pointer'
                                        }}
                                    >Cancel</button>
                                    <button
                                        onClick={() => { setShowCloseConfirm(false); endSession(); }}
                                        style={{
                                            flex: 1, padding: '10px', borderRadius: '8px',
                                            border: 'none', background: '#ef4444',
                                            color: '#fff', fontWeight: 600,
                                            fontSize: '13px', cursor: 'pointer'
                                        }}
                                    >End Chat</button>
                                </div>
                            </div>
                        </div>
                    )}

                    {!isStarted ? (
                        <div className="chat-start-screen">
                            <div className="chat-welcome-card">
                                <div className="chat-welcome-avatar">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                        <circle cx="12" cy="7" r="4"></circle>
                                    </svg>
                                </div>
                                <div className="chat-welcome-bubble">
                                    <div className="chat-welcome-header">
                                        <span className="chat-welcome-author">Suporti Globe</span>
                                        <span className="chat-welcome-status">Online</span>
                                    </div>
                                    <div className="chat-welcome-text">
                                        Mirë se vini! 👋<br/>
                                        Ju lutemi shkruani emrin tuaj më poshtë për të filluar bisedën me ekipin tonë të suportit.
                                    </div>
                                </div>
                            </div>

                            {connectionError && (
                                <p className="chat-connection-error">{connectionError}</p>
                            )}

                            <form className="chat-start-form" onSubmit={startChat}>
                                <div className="chat-input-wrapper">
                                    <label className="chat-input-label">Emri juaj</label>
                                    <input
                                        type="text"
                                        className="chat-name-input"
                                        placeholder="psh. Arben"
                                        value={guestName}
                                        onChange={(e) => setGuestName(e.target.value)}
                                        required
                                        autoFocus
                                        disabled={isConnecting}
                                    />
                                </div>
                                <button type="submit" className="chat-start-btn" disabled={!guestName.trim() || isConnecting}>
                                    {isConnecting ? 'Duke u lidhur...' : 'Fillo Bisedën →'}
                                </button>
                            </form>
                        </div>
                    ) : (
                        <>
                            <div className="chat-messages" style={{ height: isOfflineMode ? 'calc(100% - 110px)' : undefined }}>
                                {messages.length === 0 ? (
                                    <p className="no-messages">Ne zakonisht përgjigjemi brenda pak minutash.</p>
                                ) : null}
                                {messages.map((m, idx) => (
                                    <div key={m.id || idx} className={`chat-bubble ${m.senderType}`}>
                                        {m.senderType === 'operator' && m.adminName && (
                                            <div style={{ fontSize: '10.5px', fontWeight: 'bold', marginBottom: '4px', opacity: 0.9 }}>
                                                {m.adminName}
                                            </div>
                                        )}
                                        {renderWithLinks(m.content)}
                                        <div style={{ fontSize: '10.5px', marginTop: '6px', opacity: 0.7, textAlign: m.senderType === 'client' ? 'right' : 'left' }}>
                                            {new Date(m.createdAt || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </div>
                                    </div>
                                ))}

                                {/* Session ended notice */}
                                {isSessionEnded && (
                                    <div style={{
                                        textAlign: 'center', padding: '16px 12px',
                                        margin: '8px 0', borderRadius: '10px',
                                        background: '#fef2f2', border: '1px solid #fecaca'
                                    }}>
                                        <p style={{ margin: '0 0 10px', fontSize: '13px', color: '#991b1b', fontWeight: 600 }}>
                                            Kjo bisedë është mbyllur nga agjenti i mbështetjes.
                                        </p>
                                        <button
                                            onClick={() => {
                                                setIsSessionEnded(false);
                                                setIsStarted(false);
                                                setMessages([]);
                                                setSessionId(null);
                                                setGuestName('');
                                                if (socketRef.current) { socketRef.current.close(); socketRef.current = null; }
                                            }}
                                            style={{
                                                padding: '8px 16px', borderRadius: '8px',
                                                border: 'none', background: '#007da5',
                                                color: '#fff', fontWeight: 600,
                                                fontSize: '13px', cursor: 'pointer'
                                            }}
                                        >Fillo Bisedë të Re</button>
                                    </div>
                                )}

                                <div ref={messagesEndRef} />
                            </div>
                            {!isSessionEnded && (
                                <form className="chat-input-area" onSubmit={sendMessage}>
                                    <input
                                        type="text"
                                        placeholder={!isConnected ? "Duke u lidhur..." : "Shkruaj mesazhin tënd..."}
                                        value={newMessage}
                                        onChange={(e) => setNewMessage(e.target.value)}
                                        disabled={!isConnected || !sessionId}
                                    />
                                    <button type="submit" disabled={!isConnected || !newMessage.trim()}>
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>
                                    </button>
                                </form>
                            )}
                        </>
                    )}
                </div>
            )}

            {!isOpen && (
                <div className="chat-toggle-wrapper">
                    {showGreeting && (
                        <div className="chat-greeting-popup" onClick={() => { setIsOpen(true); setShowGreeting(false); }}>
                            <span>Përshëndetje, si mund t'ju ndihmojmë?</span>
                            <button
                                type="button"
                                className="chat-greeting-close"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowGreeting(false);
                                }}
                                aria-label="Mbyll"
                            >
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </button>
                        </div>
                    )}
                    <button 
                        type="button"
                        className="chat-toggle" 
                        onClick={() => { setIsOpen(true); setShowGreeting(false); }}
                        aria-label="Hap bisedën e suportit"
                    >
                        <img 
                            src="https://globe.al/uploads/media/1787836402115-kirby.webp" 
                            alt="Live Support" 
                            className="chat-toggle-img" 
                        />
                    </button>
                </div>
            )}
        </div>
    );
};

export default ChatWidget;
